import { create } from 'zustand';
import { CNSConversation } from '@/models/cns';
import { createConversation as createConversationService, deleteConversation as deleteConversationService, fetchConversationById as fetchConversationByIdService, fetchRecentConversationsForCurrentUser } from '@/services/conversationService';
import { fetchProjectConversations as fetchProjectConversationsService } from '@/services/projectService';
import { useProjectStore } from '@/stores/useProjectStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useAccessDeniedStore } from '@/stores/useAccessDeniedStore';
import { getErrorMessage, getProblemDetailCode, isViewForbiddenCode } from '@/types/problem-detail';

/** Coalesce concurrent recent-chat loads per workspace (sidebar mount + StrictMode). */
const pendingRecentByWorkspaceKey = new Map<string, Promise<void>>();
interface ConversationStore {
  // State - global conversations (for backward compatibility)
  conversations: CNSConversation[];
  loading: boolean;
  error: string | null;
  isInitialized: boolean;

  // State - project-scoped conversations
  conversationsByProject: Record<string, CNSConversation[]>;
  loadingByProject: Record<string, boolean>;
  errorByProject: Record<string, string | null>;

  // Sidebar recent conversations (workspace-scoped)
  recentConversations: CNSConversation[];
  recentLoading: boolean;
  recentError: string | null;
  _lastFetchedRecentWorkspaceId: string | null;

  // Actions - global (for backward compatibility)
  fetchAllConversations: () => Promise<void>;
  setConversations: (conversations: CNSConversation[]) => void;
  addConversation: (conversation: CNSConversation, options?: {
    addToProjectBucket?: boolean;
  }) => void;
  updateConversation: (conversationId: string, updates: Partial<CNSConversation>) => void;
  removeConversation: (conversationId: string) => void;
  createConversation: (title: string, projectId: string) => Promise<CNSConversation>;
  deleteConversation: (conversationId: string) => Promise<void>;
  clearConversations: () => void;
  clearAll: () => void;

  // Actions - project-scoped with deduplication
  fetchProjectConversations: (projectId: string) => Promise<void>;
  /** Refetches project conversations and replaces the cached project bucket (e.g. after branch). */
  refreshProjectConversations: (projectId: string) => Promise<void>;
  getProjectConversations: (projectId: string) => CNSConversation[];
  clearProjectConversations: (projectId: string) => void;
  fetchRecentConversations: () => Promise<void>;
  clearRecentConversations: () => void;

  // Resolve a conversation id into conversation + project context.
  // preferredProjectId narrows lookup first to reduce cross-project scanning.
  resolveConversationById: (conversationId: string, preferredProjectId?: string) => Promise<{
    conversation: CNSConversation;
    projectId: string;
  } | null>;

  // Getters
  getConversationById: (id: string) => CNSConversation | undefined;
  getConversationsByProjectId: (projectId: string) => CNSConversation[];
  getRecentConversations: (limit?: number) => CNSConversation[];
}
export const useConversationStore = create<ConversationStore>((set, get) => ({
  // Initial state - global
  conversations: [],
  loading: false,
  error: null,
  isInitialized: false,
  // Initial state - project-scoped
  conversationsByProject: {},
  loadingByProject: {},
  errorByProject: {},
  recentConversations: [],
  recentLoading: false,
  recentError: null,
  _lastFetchedRecentWorkspaceId: null,
  fetchRecentConversations: async () => {
    const workspaceId = useWorkspaceStore.getState().currentWorkspaceId;
    const key = workspaceId ?? "__none__";
    let pending = pendingRecentByWorkspaceKey.get(key);
    if (!pending) {
      pending = (async () => {
        set({
          recentLoading: true,
          recentError: null
        });
        try {
          await useProjectStore.getState().fetchProjects();
          const ws = useWorkspaceStore.getState().currentWorkspaceId;
          const page = await fetchRecentConversationsForCurrentUser(0, 10, ws ?? undefined);
          set({
            recentConversations: page.content ?? [],
            recentLoading: false,
            recentError: null,
            _lastFetchedRecentWorkspaceId: ws
          });
        } catch (error) {
          set({
            recentError: getErrorMessage(error, "Failed to fetch recent conversations"),
            recentLoading: false
          });
          console.error("Error loading recent conversations:", error);
        } finally {
          pendingRecentByWorkspaceKey.delete(key);
        }
      })();
      pendingRecentByWorkspaceKey.set(key, pending);
    }
    await pending;
  },
  clearRecentConversations: () => {
    set({
      recentConversations: [],
      recentLoading: false,
      recentError: null,
      _lastFetchedRecentWorkspaceId: null
    });
  },
  // Fetch all conversations from all projects (like projects do)
  fetchAllConversations: async () => {
    const state = get();

    // Don't fetch if already loading
    if (state.loading) return;

    // Don't fetch if already initialized and have conversations
    if (state.isInitialized && state.conversations.length > 0) return;
    set({
      loading: true,
      error: null
    });
    try {
      // Get all conversations from all projects
      const allConversations: CNSConversation[] = [];

      // We need to get projects first to fetch conversations
      // For now, we'll try to get conversations from the current project context
      // This will be called from components that already have projects loaded

      // Note: This is a simplified approach - in a real app, you might want to
      // fetch all conversations from all projects, but for now we'll rely on
      // the conversation being loaded by the chat components

      set({
        conversations: allConversations,
        loading: false,
        error: null,
        isInitialized: true
      });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to fetch conversations',
        loading: false
      });
    }
  },
  // Set conversations (used when fetching from API)
  setConversations: (conversations: CNSConversation[]) => {
    set({
      conversations,
      isInitialized: true
    });
  },
  // Fetch conversations for a specific project with deduplication
  // Uses Zustand state to prevent duplicate concurrent requests and avoid
  // duplicate requests from React Strict Mode double-invocation in development.
  // Data persists in memory for same-session navigation.
  fetchProjectConversations: async (projectId: string) => {
    const state = get();

    // Skip if we already have cached conversations for this project (avoids
    // duplicate calls from Strict Mode and redundant refetches in same session).
    if (state.conversationsByProject[projectId] !== undefined) {
      return;
    }

    // Atomic check: if already loading, return early (prevents duplicate requests)
    // Components will re-render when data arrives via Zustand subscriptions
    if (state.loadingByProject[projectId]) {
      return;
    }

    // Atomically set loading state to prevent concurrent requests
    set(state => {
      if (state.loadingByProject[projectId]) return state;
      return {
        loadingByProject: {
          ...state.loadingByProject,
          [projectId]: true
        },
        errorByProject: {
          ...state.errorByProject,
          [projectId]: null
        }
      };
    });
    const currentState = get();
    if (!currentState.loadingByProject[projectId]) return;
    try {
      const conversations = await fetchProjectConversationsService(projectId);
      // Deduplicate by id so React list keys are unique (avoids duplicate key when same conversation appears from API + direct-shared path)
      const seenIds = new Set<string>();
      const dedupedConversations = conversations.filter((c: CNSConversation) => {
        if (seenIds.has(c.id)) return false;
        seenIds.add(c.id);
        return true;
      });

      // Update both project-scoped store and global conversations list
      set(state => {
        // Upsert the fresh actor-specific rows. An older direct-share projection may
        // already be present globally when project access is granted later.
        const fetchedConversationIds = new Set(dedupedConversations.map(conversation => conversation.id));
        const mergedConversations = [...dedupedConversations, ...state.conversations.filter(conversation => !fetchedConversationIds.has(conversation.id))];
        return {
          conversationsByProject: {
            ...state.conversationsByProject,
            [projectId]: dedupedConversations
          },
          conversations: mergedConversations,
          loadingByProject: {
            ...state.loadingByProject,
            [projectId]: false
          },
          errorByProject: {
            ...state.errorByProject,
            [projectId]: null
          },
          isInitialized: true
        };
      });
    } catch (error: any) {
      set(state => ({
        loadingByProject: {
          ...state.loadingByProject,
          [projectId]: false
        },
        errorByProject: {
          ...state.errorByProject,
          [projectId]: error.message || 'Failed to fetch conversations'
        }
      }));
    }
  },
  refreshProjectConversations: async (projectId: string) => {
    if (get().loadingByProject[projectId]) {
      return;
    }
    set(state => ({
      loadingByProject: {
        ...state.loadingByProject,
        [projectId]: true
      },
      errorByProject: {
        ...state.errorByProject,
        [projectId]: null
      }
    }));
    try {
      const conversations: CNSConversation[] = await fetchProjectConversationsService(projectId);
      const seenIds = new Set<string>();
      const dedupedConversations = conversations.filter(c => {
        if (seenIds.has(c.id)) return false;
        seenIds.add(c.id);
        return true;
      });
      set(state => {
        const dedupedIds = new Set(dedupedConversations.map(c => c.id));
        const otherConversations = state.conversations.filter(c => c.projectId !== projectId && !dedupedIds.has(c.id));
        return {
          conversationsByProject: {
            ...state.conversationsByProject,
            [projectId]: dedupedConversations
          },
          conversations: [...dedupedConversations, ...otherConversations],
          loadingByProject: {
            ...state.loadingByProject,
            [projectId]: false
          },
          errorByProject: {
            ...state.errorByProject,
            [projectId]: null
          },
          isInitialized: true
        };
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch conversations';
      set(state => ({
        loadingByProject: {
          ...state.loadingByProject,
          [projectId]: false
        },
        errorByProject: {
          ...state.errorByProject,
          [projectId]: message
        }
      }));
    }
  },
  // Get conversations for a project (from store)
  getProjectConversations: (projectId: string) => {
    return get().conversationsByProject[projectId] || [];
  },
  // Clear conversations for a project
  clearProjectConversations: (projectId: string) => {
    set(state => {
      const newConversationsByProject = {
        ...state.conversationsByProject
      };
      const newLoadingByProject = {
        ...state.loadingByProject
      };
      const newErrorByProject = {
        ...state.errorByProject
      };
      delete newConversationsByProject[projectId];
      delete newLoadingByProject[projectId];
      delete newErrorByProject[projectId];

      // Also remove from global conversations
      const filteredConversations = state.conversations.filter(conv => conv.projectId !== projectId);
      return {
        conversationsByProject: newConversationsByProject,
        loadingByProject: newLoadingByProject,
        errorByProject: newErrorByProject,
        conversations: filteredConversations
      };
    });
  },
  // Add or refresh a conversation. Replacing an existing row is important because
  // the backend access projection is actor-specific and may change after sharing.
  // When addToProjectBucket is false (e.g. direct-shared), only update the global list.
  addConversation: (conversation, options) => {
    const addToProjectBucket = options?.addToProjectBucket !== false;
    set(state => {
      const alreadyInGlobal = state.conversations.some(c => c.id === conversation.id);
      const updatedConversations = alreadyInGlobal ? state.conversations.map(current => current.id === conversation.id ? conversation : current) : [conversation, ...state.conversations];
      if (!addToProjectBucket) {
        return {
          conversations: updatedConversations
        };
      }
      const projectConversations = state.conversationsByProject[conversation.projectId] || [];
      const alreadyInProject = projectConversations.some(c => c.id === conversation.id);
      const updatedProjectConversations = alreadyInProject ? projectConversations.map(current => current.id === conversation.id ? conversation : current) : [conversation, ...projectConversations];
      return {
        conversations: updatedConversations,
        conversationsByProject: {
          ...state.conversationsByProject,
          [conversation.projectId]: updatedProjectConversations
        }
      };
    });
  },
  // Update a conversation in the store
  updateConversation: (conversationId, updates) => {
    set(state => {
      // Find the conversation to get its projectId
      const conversation = state.conversations.find(c => c.id === conversationId);
      const projectId = conversation?.projectId;

      // Update global conversations
      const updatedConversations = state.conversations.map(conv => conv.id === conversationId ? {
        ...conv,
        ...updates
      } : conv);

      // Update project-scoped cache if projectId exists
      const updatedState: any = {
        conversations: updatedConversations
      };
      if (projectId && state.conversationsByProject[projectId]) {
        updatedState.conversationsByProject = {
          ...state.conversationsByProject,
          [projectId]: state.conversationsByProject[projectId].map(conv => conv.id === conversationId ? {
            ...conv,
            ...updates
          } : conv)
        };
      }
      return updatedState;
    });
  },
  // Remove a conversation from the store
  removeConversation: conversationId => {
    set(state => {
      // Find the conversation to get its projectId
      const conversation = state.conversations.find(c => c.id === conversationId);
      const projectId = conversation?.projectId;

      // Remove from global conversations
      const filteredConversations = state.conversations.filter(conv => conv.id !== conversationId);

      // Remove from project-scoped cache if projectId exists
      const updatedState: any = {
        conversations: filteredConversations
      };
      if (projectId && state.conversationsByProject[projectId]) {
        updatedState.conversationsByProject = {
          ...state.conversationsByProject,
          [projectId]: state.conversationsByProject[projectId].filter(conv => conv.id !== conversationId)
        };
      }
      return updatedState;
    });
  },
  // Create a new conversation
  createConversation: async (title: string, projectId: string) => {
    set({
      loading: true,
      error: null
    });
    try {
      const newConversation = await createConversationService(title, projectId);
      set(state => ({
        conversations: [newConversation, ...state.conversations],
        conversationsByProject: {
          ...state.conversationsByProject,
          [projectId]: [newConversation, ...(state.conversationsByProject[projectId] || [])]
        },
        loading: false
      }));
      get().fetchRecentConversations().catch(() => {});
      return newConversation;
    } catch (error: any) {
      set({
        error: error.message || 'Failed to create conversation',
        loading: false
      });
      throw error;
    }
  },
  // Delete a conversation
  deleteConversation: async (conversationId: string) => {
    set({
      loading: true,
      error: null
    });
    try {
      await deleteConversationService(conversationId);
      set(state => ({
        conversations: state.conversations.filter(conv => conv.id !== conversationId),
        conversationsByProject: Object.fromEntries(Object.entries(state.conversationsByProject).map(([projectId, list]) => [projectId, list.filter(conv => conv.id !== conversationId)])),
        loading: false
      }));
      get().fetchRecentConversations().catch(() => {});
    } catch (error: any) {
      set({
        error: error.message || 'Failed to delete conversation',
        loading: false
      });
      throw error;
    }
  },
  // Clear all conversations
  clearConversations: () => {
    set({
      conversations: [],
      isInitialized: false
    });
  },
  // Clear all conversation data
  clearAll: () => {
    set({
      conversations: [],
      loading: false,
      error: null,
      isInitialized: false,
      conversationsByProject: {},
      loadingByProject: {},
      errorByProject: {},
      recentConversations: [],
      recentLoading: false,
      recentError: null,
      _lastFetchedRecentWorkspaceId: null
    });
  },
  // Resolve conversation by id: find in store or fetch project conversations.
  // If preferredProjectId is provided, check that project first before global scan.
  resolveConversationById: async (conversationId: string, preferredProjectId?: string): Promise<{
    conversation: CNSConversation;
    projectId: string;
  } | null> => {
    const state = get();
    const existing = state.conversations.find(conv => conv.id === conversationId);
    if (existing && existing.projectId && existing.access) {
      useAccessDeniedStore.getState().clearAccessDenied();
      return {
        conversation: existing,
        projectId: existing.projectId
      };
    }

    // Direct fetch by id first. This supports direct-shared conversations without
    // requiring project-level list access (which may legitimately return 403).
    try {
      const conversation = await fetchConversationByIdService(conversationId);
      get().addConversation(conversation, {
        addToProjectBucket: false
      });
      const resolvedProjectId = conversation.projectId ?? preferredProjectId ?? "";
      if (resolvedProjectId) {
        const project = useProjectStore.getState().getProjectById(resolvedProjectId);
        if (project) useProjectStore.getState().setCurrentProject(project);
      }
      useAccessDeniedStore.getState().clearAccessDenied();
      return {
        conversation,
        projectId: resolvedProjectId
      };
    } catch (err) {
      const code = getProblemDetailCode(err);
      if (code && isViewForbiddenCode(code)) {
        throw err;
      }
      // Fall through to project scan as a compatibility fallback (e.g. 404).
    }
    const fetchProjectConversations = get().fetchProjectConversations;
    const getProjectConversations = get().getProjectConversations;
    if (preferredProjectId) {
      try {
        await fetchProjectConversations(preferredProjectId);
        const preferredConversations = getProjectConversations(preferredProjectId);
        const preferredMatch = preferredConversations.find((c: CNSConversation) => c.id === conversationId);
        if (preferredMatch) {
          get().addConversation(preferredMatch);
          useAccessDeniedStore.getState().clearAccessDenied();
          return {
            conversation: preferredMatch,
            projectId: preferredProjectId
          };
        }
      } catch {
        // Continue to broader scan.
      }
    }
    const {
      projects,
      fetchProjects
    } = useProjectStore.getState();
    let projectList = projects;
    if (projectList.length === 0) {
      await fetchProjects();
      projectList = useProjectStore.getState().projects;
    }
    for (const project of projectList) {
      if (preferredProjectId && project.id === preferredProjectId) {
        continue;
      }
      try {
        await fetchProjectConversations(project.id);
        const conversations = getProjectConversations(project.id);
        const found = conversations.find((c: CNSConversation) => c.id === conversationId);
        if (found) {
          get().addConversation(found);
          useAccessDeniedStore.getState().clearAccessDenied();
          return {
            conversation: found,
            projectId: project.id
          };
        }
      } catch {
        // Skip projects that fail
      }
    }
    return null;
  },
  // Get conversation by ID
  getConversationById: (id: string) => {
    return get().conversations.find(conv => conv.id === id);
  },
  // Get conversations by project ID (prefer project-scoped cache, fallback to global). Deduplicated by id for stable React keys.
  getConversationsByProjectId: (projectId: string) => {
    const state = get();
    let list: CNSConversation[];
    if (state.conversationsByProject[projectId]) {
      list = state.conversationsByProject[projectId];
    } else {
      list = state.conversations.filter(conv => conv.projectId === projectId);
    }
    const seen = new Set<string>();
    return list.filter(c => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  },
  // Get recent conversations for the sidebar rail
  getRecentConversations: (limit = 5) => {
    return get().recentConversations.slice(0, limit);
  }
}));

// Create optimized selectors to avoid unnecessary re-renders
export const useConversations = () => {
  return useConversationStore(_temp);
};
export const useConversationsLoading = () => {
  return useConversationStore(_temp2);
};
export const useConversationsError = () => {
  return useConversationStore(_temp3);
};
export const useConversationsInitialized = () => {
  return useConversationStore(_temp4);
};
function _temp(state) {
  return state.conversations;
}
function _temp2(state) {
  return state.loading;
}
function _temp3(state) {
  return state.error;
}
function _temp4(state) {
  return state.isInitialized;
}